import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import { LegacyDevice } from 'console/device/device';
import { Capability } from 'console/device/device-capability';
import { requestDeviceUpdate } from 'console/redux/devices/items/actions';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { select_ui_settings } from 'console/redux/settings-actions';
import { VehicleDeviceState } from 'console/shared/contexts/vehicle-device/state/types';
import { DeviceConfig } from 'console/shared/contexts/device/config/types';
import { AudioSettings } from '../audio-settings';
import { VehicleUIControl } from '../vehicle-ui-control';
import { ServiceSensorsSettings } from '../service-sensors-settings';
export const VehicleSettings = memo(function VehicleSettings() {
    const ui_settings = useAppSelector(select_ui_settings);
    const { service_mode, silent_mode } = useContext(VehicleDeviceState);
    const { device_id, capabilities } = useContext(DeviceConfig);
    const device_audio = useMemo(() => {
        const guard_audio = LegacyDevice.has_capability(capabilities, Capability.has_guard_state);
        const auto_start_audio = LegacyDevice.has_capability(capabilities, Capability.has_autostart);
        const siren_audio = LegacyDevice.has_capability(capabilities, Capability.has_siren_control);
        const engine_audio = LegacyDevice.has_capability(capabilities, Capability.has_car_sensors);
        return { guard_audio, siren_audio, engine_audio, auto_start_audio };
    }, [capabilities]);
    const audio_settings = useMemo(() => {
        const { guard_audio, siren_audio, engine_audio, auto_start_audio } = device_audio;
        return guard_audio || siren_audio || engine_audio || auto_start_audio;
    }, [device_audio]);
    const { device_state_sounds } = useMemo(() => {
        return {
            device_state_sounds: ui_settings.device_state_sounds?.[device_id],
        };
    }, [ui_settings, device_id]);
    const onUpdateDevice = useAction(requestDeviceUpdate);
    const changeSilentMode = useCallback((value) => {
        onUpdateDevice(device_id, { silent_mode: value }, {
            ignore_server_response: true,
            obey_only_last_response: true,
        });
    }, [device_id]);
    const onChangeSilentMode = useCallback((siren_or_sensor, key, value) => {
        if (silent_mode != null)
            changeSilentMode({
                ...silent_mode,
                [siren_or_sensor]: {
                    ...silent_mode[siren_or_sensor],
                    [key]: value,
                },
            });
    }, [changeSilentMode, silent_mode]);
    const onSetServiceMode = useCallback((device_id, value) => {
        onUpdateDevice(device_id, { service_mode: value }, {
            ignore_server_response: true,
            obey_only_last_response: true,
        });
    }, []);
    const service_notification = useMemo(() => !!(silent_mode && service_mode), [service_mode, silent_mode]);
    const { siren_notification, sensors_notification } = useMemo(() => {
        let notifications = { siren_notification: false, sensors_notification: false };
        if (silent_mode) {
            const silent_mode_siren = silent_mode.siren;
            const silent_mode_sensors = silent_mode.sensors;
            if (silent_mode_siren.alarm) {
                notifications = {
                    ...notifications,
                    siren_notification: true,
                };
            }
            if (silent_mode_sensors.shock ||
                silent_mode_sensors.tilt ||
                silent_mode_sensors.weak_shock) {
                notifications = {
                    ...notifications,
                    sensors_notification: true,
                };
            }
        }
        return notifications;
    }, [silent_mode]);
    if (!audio_settings) {
        return null;
    }
    return (_jsxs(Box, { position: "absolute", right: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", zIndex: 10, children: [silent_mode && (_jsx(ServiceSensorsSettings, { service_notification: service_notification, sensors_notification: sensors_notification, onSetServiceMode: onSetServiceMode, onChangeSilentMode: onChangeSilentMode, silent_mode: silent_mode })), _jsx(AudioSettings, { without_silent_mode: silent_mode == null, is_error: siren_notification, has_handsfree: LegacyDevice.has_capability(capabilities, Capability.has_handsfree), app_sounds: device_state_sounds?.app_sounds, device_audio: device_audio, onChangeSilentMode: onChangeSilentMode }), _jsx(VehicleUIControl, {})] }));
});
